import React, { useMemo } from 'react';
import { ExecutionSchedule } from '../types';
import { ExecutionCard } from './ExecutionCard';
import {
  format,
  parseISO,
  startOfWeek,
  addDays,
  isSameDay,
  isWithinInterval,
  startOfDay,
  endOfDay,
} from 'date-fns';

interface ScheduleCalendarProps {
  executions: ExecutionSchedule[];
  selectedDate?: Date;
  onDateSelect?: (date: Date) => void;
  onStatusClick?: (executionId: string, currentStatus: string) => void;
}

export const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  executions,
  selectedDate = new Date(),
  onDateSelect,
  onStatusClick,
}) => {
  // Get week start (Monday)
  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  // Group executions by day
  const executionsByDay = useMemo(() => {
    const grouped: Record<string, ExecutionSchedule[]> = {};
    weekDays.forEach((day) => {
      const dayKey = format(day, 'yyyy-MM-dd');
      grouped[dayKey] = executions.filter((exec) => {
        const execTime = exec.timestamp || exec.scheduled_time;
        return execTime && isSameDay(parseISO(execTime), day);
      });
    });
    return grouped;
  }, [executions, weekDays]);

  // Get day executions for selected date
  const selectedDayExecutions = useMemo(() => {
    return executions.filter((exec) => {
      const execTime = exec.timestamp || exec.scheduled_time;
      return execTime && isWithinInterval(parseISO(execTime), {
        start: startOfDay(selectedDate),
        end: endOfDay(selectedDate),
      });
    });
  }, [executions, selectedDate]);

  return (
    <div className="space-y-4">
      {/* Week View */}
      <div className="grid grid-cols-7 gap-2">
        {weekDays.map((day) => {
          const dayKey = format(day, 'yyyy-MM-dd');
          const dayExecutions = executionsByDay[dayKey] || [];
          const isSelected = isSameDay(day, selectedDate);
          const isToday = isSameDay(day, new Date());

          return (
            <div
              key={dayKey}
              onClick={() => onDateSelect?.(day)}
              className={`
                border rounded-lg p-3 cursor-pointer transition-all
                ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}
                ${isToday ? 'border-blue-400 border-2' : 'border-gray-200'}
              `}
            >
              {/* Day Header */}
              <div className="text-center mb-2">
                <div className="text-xs text-gray-500 uppercase">
                  {format(day, 'EEE')}
                </div>
                <div className={`text-lg font-semibold ${isToday ? 'text-blue-600' : 'text-gray-800'}`}>
                  {format(day, 'd')}
                </div>
              </div>

              {/* Execution Count & Status Summary */}
              {dayExecutions.length > 0 ? (
                <div className="space-y-1">
                  <div className="text-xs text-center text-gray-600 font-medium">
                    {dayExecutions.length} task{dayExecutions.length > 1 ? 's' : ''}
                  </div>
                  <div className="flex justify-center gap-1">
                    {dayExecutions.filter((e) => e.status === 'pending').length > 0 && (
                      <span className="text-xs">⏳{dayExecutions.filter((e) => e.status === 'pending').length}</span>
                    )}
                    {dayExecutions.filter((e) => e.status === 'running').length > 0 && (
                      <span className="text-xs">▶️{dayExecutions.filter((e) => e.status === 'running').length}</span>
                    )}
                    {dayExecutions.filter((e) => e.status === 'completed').length > 0 && (
                      <span className="text-xs">✅{dayExecutions.filter((e) => e.status === 'completed').length}</span>
                    )}
                    {dayExecutions.filter((e) => e.status === 'failed').length > 0 && (
                      <span className="text-xs">❌{dayExecutions.filter((e) => e.status === 'failed').length}</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-center text-gray-400">No tasks</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected Day Detail */}
      {selectedDayExecutions.length > 0 && (
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-3">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            <span className="ml-2 text-sm text-gray-500">
              ({selectedDayExecutions.length} execution{selectedDayExecutions.length > 1 ? 's' : ''})
            </span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {selectedDayExecutions
              .sort((a, b) => {
                const timeA = parseISO(a.timestamp || a.scheduled_time || '');
                const timeB = parseISO(b.timestamp || b.scheduled_time || '');
                return timeA.getTime() - timeB.getTime();
              })
              .map((execution) => (
                <ExecutionCard
                  key={execution.execution_id}
                  execution={execution}
                  onStatusClick={onStatusClick}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
